import type { Row } from '@tanstack/react-table';
import { History as HistoryIcon, HistoryRounded } from 'components/icons';
import { Popover, PopoverContent, PopoverTrigger } from 'components/ui/popover';
import { useMemo, useState } from 'react';
import CopyToClipboard from 'react-copy-to-clipboard';

import { skipToken } from '@reduxjs/toolkit/query';
import ContentCopyFilled from 'assets/images/icons/ContentCopyFilled.svg?react';
import CircularLoader from 'components/CircularLoader';
import EmptyState from 'components/EmptyState';
import BasicTable from 'components/tables/BasicTable';
import type { DataTableColumnDef } from 'components/tables/data-table';
import { formatNumber } from 'components/util/HoursDisplay';
import { timecardSecondsToHours } from 'pages/timecards/utils/timecardUtils';
import { useGetMatterByIdQuery } from 'store/api/matters';
import { useGetMatterTimecardHistoryQuery } from 'store/api/timecards';
import { MatterPartyRoleType } from 'types/matters';
import { OfficialyNamedEntityMinDto } from 'types/other.ts';
import { TimecardMatterHistoryItem } from 'types/timecards';
import { t } from 'utils/getTranslations';
import { cn } from 'utils/cn';
import { useTimecardPrecision } from 'utils/useTimecardPrecision';
import { FIELD_GLYPH_BUTTON_CLASS } from './buttonStyle';
import { CallForActionButton, CallForActionButtonTooltip } from './CallForActionButton';

const NO_RECORDS_ELEMENT = <EmptyState size="inline" titleId="history.no-records" surface="timecard-history" />;

type Props = {
    matterId?: number | null;
    handleTimecardCopy?: (timecard: TimecardMatterHistoryItem) => void | Promise<void>;
    className?: string;
    /** Drawn as a glyph inside the field, beside the field's other glyph buttons, rather than beside the field. */
    inField?: boolean;
};

type RelatedContact = Pick<OfficialyNamedEntityMinDto, 'officialName'>;

export default function TimecardHistoryButton({ matterId, handleTimecardCopy, className, inField = false }: Props) {
    const [isOpen, setIsOpen] = useState(false);
    const { isTwoDecimalDisplayEnabled, displayDecimalScale } = useTimecardPrecision();

    const { data: timecards, isLoading: isLoadingTimecards } = useGetMatterTimecardHistoryQuery(matterId ?? skipToken);

    const { data: matterDetailsData, isLoading: isLoadingMatterDetails } = useGetMatterByIdQuery(
        matterId?.toString() ?? skipToken
    );

    const matterDetails = !matterId ? undefined : matterDetailsData;

    const timecardColumns = useMemo<DataTableColumnDef<TimecardMatterHistoryItem>[]>(
        () => [
            {
                header: t('date'),
                accessorKey: 'timecardDate',
                cell: ({ getValue }) => getValue() ?? '',
                meta: {
                    width: 1,
                    className: 'shrink',
                },
            },
            {
                header: t('timecards.history.author'),
                id: 'employee.name',
                accessorFn: (row) => row.employee?.name,
                cell: ({ getValue }) => getValue<string | undefined>() ?? '',
                meta: {
                    width: 1,
                    className: 'shrink',
                },
            },
            {
                header: t('timecards.history.used-time'),
                accessorKey: 'totalTimeInSeconds',
                cell: ({ row }) => {
                    const { totalTimeInSeconds, isRestricted } = row.original;
                    if (isRestricted) return '';
                    return (
                        <div className="flex flex-col">
                            {formatNumber(
                                timecardSecondsToHours(totalTimeInSeconds ?? 0, isTwoDecimalDisplayEnabled),
                                displayDecimalScale
                            )}
                        </div>
                    );
                },
                meta: {
                    width: 1,
                    className: 'shrink',
                },
            },
            {
                header: t('timecards.history.spec-of-work'),
                accessorKey: 'description',
                cell: ({ row }) => {
                    const { isRestricted, description } = row.original;
                    if (isRestricted) return t('timecards.history.restricted-access');
                    return description ?? '';
                },
            },
            {
                id: '_action',
                cell: ({ row }) => {
                    const timecard = row.original;
                    if (timecard.isRestricted) return null;
                    return (
                        <div className="flex justify-end">
                            <CallForActionButtonTooltip title={t('timecards.copy-description')}>
                                <span className="inline-flex">
                                    <CopyToClipboard
                                        text={timecard.description || ''}
                                        onCopy={() => {
                                            void (async () => {
                                                try {
                                                    await handleTimecardCopy?.(timecard);
                                                    setIsOpen((prev) => !prev);
                                                } catch (e) {
                                                    console.error(e);
                                                }
                                            })();
                                        }}
                                    >
                                        <CallForActionButton
                                            variant="contained"
                                            shape="rounded"
                                            color="info"
                                            aria-label={t('timecards.copy-description')}
                                            className="p-1.5"
                                        >
                                            <ContentCopyFilled />
                                        </CallForActionButton>
                                    </CopyToClipboard>
                                </span>
                            </CallForActionButtonTooltip>
                        </div>
                    );
                },
                meta: {
                    width: 1,
                    className: 'shrink',
                },
            },
        ],
        [handleTimecardCopy, isTwoDecimalDisplayEnabled, displayDecimalScale]
    );

    const getRowStyle = (row: Row<TimecardMatterHistoryItem>) =>
        row.original.isRestricted ? { backgroundColor: 'var(--color-grey-200)' } : undefined;

    const relatedContacts = (function constructRelatedContacts() {
        if (!matterDetails) return [];

        const contacts: Pick<OfficialyNamedEntityMinDto, 'officialName'>[] = [];

        if (matterDetails.primaryContactParty) {
            contacts.push({ officialName: matterDetails.primaryContactParty.officialName });
        }

        matterDetails?.parties
            ?.filter(
                (party) =>
                    party.matterPartyRoleTypeCode === MatterPartyRoleType.CLIENT_REPRESENTED ||
                    party.matterPartyRoleTypeCode === MatterPartyRoleType.OTHER_PARTY ||
                    party.matterPartyRoleTypeCode === MatterPartyRoleType.JUDGE
            )
            .forEach((party) => contacts.push({ officialName: party.party.officialName }));

        return contacts;
    })();

    const caseContactsColumns = useMemo<DataTableColumnDef<RelatedContact>[]>(
        () => [
            {
                header: t('timecards.history.case-related-contacts'),
                accessorKey: 'officialName',
                cell: ({ getValue }) => getValue() ?? '',
            },
            {
                id: '_action',
                cell: ({ row }) => {
                    const { officialName } = row.original;
                    return (
                        <div className="flex justify-end">
                            <CallForActionButtonTooltip title={t('timecards.copy-name')}>
                                <span className="inline-flex">
                                    <CopyToClipboard
                                        text={officialName || ''}
                                        onCopy={(_, result) => {
                                            if (result) {
                                                setIsOpen(!isOpen);
                                            }
                                        }}
                                    >
                                        <CallForActionButton
                                            variant="contained"
                                            shape="rounded"
                                            color="info"
                                            aria-label={t('timecards.copy-name')}
                                            className="p-1.5"
                                        >
                                            <ContentCopyFilled />
                                        </CallForActionButton>
                                    </CopyToClipboard>
                                </span>
                            </CallForActionButtonTooltip>
                        </div>
                    );
                },
            },
        ],
        [isOpen]
    );

    return (
        <Popover open={isOpen} onOpenChange={setIsOpen}>
            <CallForActionButtonTooltip title={t('history.history')}>
                <PopoverTrigger
                    render={
                        inField ? (
                            <button
                                type="button"
                                aria-label={t('history.history')}
                                className={cn(FIELD_GLYPH_BUTTON_CLASS, className)}
                            >
                                <HistoryRounded className="h-5 w-auto" />
                            </button>
                        ) : (
                            <CallForActionButton
                                variant="contained"
                                shape="rounded"
                                color="info"
                                aria-label={t('history.history')}
                                className={cn('mt-[9px] self-center', className)}
                            >
                                <HistoryIcon />
                            </CallForActionButton>
                        )
                    }
                />
            </CallForActionButtonTooltip>
            <PopoverContent
                align="end"
                sideOffset={8}
                // Opened from inside the tracker drawer and modals, so it stacks above them.
                positionerClassName="z-(--z-tooltip)"
                className="w-[696px] max-w-[calc(100vw-2rem)] gap-0 rounded p-0 shadow-xl ring-0"
            >
                {isLoadingTimecards || isLoadingMatterDetails ? (
                    <CircularLoader className="static py-4" />
                ) : (
                    <>
                        <BasicTable
                            columns={timecardColumns}
                            data={timecards?.content ?? []}
                            stickyHeader
                            containerClassName="max-h-[325px]"
                            className="[&_.shrink]:whitespace-nowrap [&_td]:px-0 [&_td]:py-1.5 [&_th]:px-0 [&_th]:py-1.5 [&_th]:whitespace-nowrap"
                            wrapperClassName="rounded-b-none"
                            noContentJSX={NO_RECORDS_ELEMENT}
                            getRowStyle={getRowStyle}
                        />
                        <BasicTable
                            columns={caseContactsColumns}
                            data={relatedContacts}
                            stickyHeader
                            containerClassName="max-h-[325px]"
                            wrapperClassName="rounded-t-none"
                            noContentJSX={NO_RECORDS_ELEMENT}
                        />
                    </>
                )}
            </PopoverContent>
        </Popover>
    );
}
